/**
 * SentReplySweepAction — the add-in's transport for the shared
 * `SentReplySweepCard`.
 *
 * ora has a file of the same name doing the same job with `useFetcher`. That
 * duplication is the point: the CARD is shared so the two panes cannot drift in
 * what they say or how they look, and only the two things that genuinely cannot
 * be shared — the local-dev gate and the way each app reaches a backend — live
 * in a per-host file.
 *
 * GATE. `isDevBuild()` — `import.meta.env.DEV`, the add-in's own dev-only
 * convention (see `DevToolsFooter`): true only under `npm run dev`, false for a
 * build of any environment. It goes through `../devBuild` rather than being read
 * inline because the pane's Jest transform rewrites `import.meta.env.DEV` to a
 * literal `false`, which would leave the enabled branch untestable. ora gates on
 * `process.env.NODE_ENV` instead — different build systems, and neither
 * expression is correct in the other's bundle, which is exactly why this check
 * cannot live in the shared card.
 *
 * TRANSPORT. `orchestratorApi` → Product API → substrate `/admin/sent-reply/sweep`,
 * the same three-hop path `checkEdocs` already uses for the substrate's other
 * admin route. The pane has no Remix router and must never import ora's
 * resource route.
 */

import { useCallback, useState } from "react";

import {
  SentReplySweepCard,
  type SentReplySweepOutcome,
} from "@product-app/common/workflows/components/SentReplySweepCard";
import { orchestratorApi } from "../services/orchestratorApi";
import { isDevBuild } from "../devBuild";

export interface SentReplySweepActionProps {
  /** The instance's status; the card renders only at pending_email_send. */
  status: string | null | undefined;
}

export default function SentReplySweepAction({
  status,
}: SentReplySweepActionProps) {
  // Hooks first, gate after — an early return above a hook is a
  // conditional-hook violation even when the condition never changes.
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<SentReplySweepOutcome | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runSweep = useCallback(async () => {
    setPending(true);
    // Clear the previous outcome as the new pass starts: leaving last run's
    // counts on screen next to a spinner reads as though they were just
    // produced.
    setError(null);
    setResult(null);
    try {
      setResult(await orchestratorApi.runSentReplySweep());
    } catch (err) {
      // ApiError carries the Product API's message, which carries the
      // substrate's own body ("forbidden" vs "sweep_failed" need different
      // fixes). Never collapse it to a generic failure string.
      setError(err instanceof Error ? err.message : "The sweep failed.");
    } finally {
      setPending(false);
    }
  }, []);

  if (!isDevBuild()) return null;

  return (
    <SentReplySweepCard
      status={status}
      pending={pending}
      result={result}
      error={error}
      onRunSweep={runSweep}
    />
  );
}
